import type { IDivisionResponse, IDivisionsPayload } from "../../myTeam/types/myTeam";
import type { ITeams } from "./types/teams";

export {
  normalizeDivisionResponse,
  normalizeDivisionList,
} from "../../myTeam/utils/divisionNormalize";

/** Property names the backend may use for team arrays on a division node. */
const TEAM_ARRAY_KEYS = [
  "teams",
  "teamSnapshotResponseList",
  "teamResponseList",
  "teamSnapshotResponses",
] as const;

/**
 * Returns team entries for a division/group node. Prefer the first non-empty array.
 */
export function getTeamsFromDivisionNode(division: IDivisionResponse | undefined | null): unknown[] {
  if (!division) return [];
  const rec = division as Record<string, unknown>;
  for (const key of TEAM_ARRAY_KEYS) {
    const arr = rec[key];
    if (Array.isArray(arr) && arr.length > 0) return arr;
  }
  for (const key of TEAM_ARRAY_KEYS) {
    const arr = rec[key];
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

export function extractDivisionListFromPayload(
  payload: IDivisionsPayload | IDivisionResponse[] | undefined
): IDivisionResponse[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const maybe = payload as IDivisionsPayload & {
    divisionResponses?: IDivisionResponse[];
    data?: { divisionResponseList?: IDivisionResponse[] };
  };

  return (
    maybe.divisionResponseList ??
    maybe.divisionResponses ??
    maybe.data?.divisionResponseList ??
    []
  );
}

/**
 * Normalize a raw team entry so snapshot ID is always accessible via `teamSnapshotId`.
 * Backend may return either `teamSnapshotId` (lowercase s) or `teamSnapShotId` (uppercase S).
 */
function normalizeTeamSnapshotIdKey(item: unknown): ITeams {
  if (!item || typeof item !== "object") return item as ITeams;
  const obj = item as Record<string, unknown>;
  // Prefer lowercase; fall back to uppercase S variant
  if (!obj.teamSnapshotId && obj["teamSnapShotId"]) {
    return { ...obj, teamSnapshotId: obj["teamSnapShotId"] } as unknown as ITeams;
  }
  return item as ITeams;
}

/** GET teamv2/{id} may return a bare array or a wrapper (see AddPlayersStep). */
export function extractTeamsFromGetAllResponse(raw: unknown): ITeams[] {
  if (!raw) return [];
  let arr: unknown[] = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const candidate =
      obj.teamResponseList ??
      obj.teamSnapshotResponseList ??
      obj.teamSnapshotResponses ??
      obj.data;
    arr = Array.isArray(candidate) ? candidate : [];
  }
  return arr.map(normalizeTeamSnapshotIdKey);
}
